import { ReferenceDirection } from '@ardoq/graph';
import { TraversalBuilderViewState, TripleOptions } from '../types';
import { EnforceContentWidth } from './atoms';
import { TripleOptionsList } from './TripleOptionsList';
import { EditTraversalCommands } from 'viewpointBuilder/traversals/editTraversalCommands';
import { FilterRow } from './FilterRow';
import { Box, Stack } from '@ardoq/layout';
import { ArdoqLoaderComponent, LoaderColor } from '@ardoq/loader';
import { TriplesSectionHeader } from './TriplesSectionHeader';
import { HiddenTriplesHint } from './HiddenTriplesHint';
import { t, type I18nLocale } from '@ardoq/i18n';
import styled from 'styled-components';

const OverlayContainer = styled.div`
  position: relative;
  min-height: 100%;
  width: 100%;
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.8);
`;

const StickyLoader = styled.div`
  position: sticky;
  top: 0;
  height: 0;
  z-index: 101;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

export type TriplesTogglerProps = {
  tripleOptions: TripleOptions;
  commands: EditTraversalCommands;
  triplesSortAndFiltersState: TraversalBuilderViewState['triplesSortAndFiltersState'];
  hasTripleOptions: TraversalBuilderViewState['hasTripleOptions'];
  isDisabledWholeRow: boolean;
  i18nLocale: I18nLocale;
};

export const TriplesToggler = ({
  tripleOptions,
  commands,
  triplesSortAndFiltersState,
  hasTripleOptions,
  isDisabledWholeRow,
  i18nLocale,
}: TriplesTogglerProps) => {
  const isLoadingOptionCounts =
    tripleOptions.optionCountLoadingState === 'loading';

  return (
    <OverlayContainer>
      {isLoadingOptionCounts && (
        <StickyLoader>
          <Box marginTop="large">
            <ArdoqLoaderComponent
              loaderColor={LoaderColor.BRAND}
              size={50}
              loaderText={t(
                i18nLocale,
                'viewpointBuilder:editTraversal.loading',
                'Loading reference options'
              )}
              ariaLabel={t(i18nLocale, 'loader:ariaLabel', 'loader')}
            />
          </Box>
        </StickyLoader>
      )}
      <EnforceContentWidth aria-busy={isLoadingOptionCounts}>
        <Box padding="medium">
          <Stack gap="large">
            <FilterRow
              commands={commands}
              triplesSortAndFiltersState={triplesSortAndFiltersState}
              isDisabled={
                !hasTripleOptions.incomingReferences &&
                !hasTripleOptions.outgoingReferences
              }
              i18nLocale={i18nLocale}
            />

            {!isLoadingOptionCounts && (
              <>
                <TriplesSectionHeader
                  label={t(
                    i18nLocale,
                    'viewpointBuilder:editTraversal.outgoing',
                    'Outgoing'
                  )}
                  count={tripleOptions.outgoing.length}
                />
                <TripleOptionsList
                  options={tripleOptions.outgoing}
                  hasTripleOptions={hasTripleOptions.outgoingReferences}
                  direction={ReferenceDirection.OUTGOING}
                  toggleOption={commands.toggleTripleOption}
                  selectedComponent={tripleOptions.selectedNode}
                  isDisabledWholeRow={isDisabledWholeRow}
                  i18nLocale={i18nLocale}
                />
                <HiddenTriplesHint
                  hiddenTriplesCount={
                    triplesSortAndFiltersState.outgoingTriplesHiddenCount
                  }
                  onShowAllClicked={
                    commands.toggleShowOnlyOptionsWithInstanceCounts
                  }
                  i18nLocale={i18nLocale}
                />

                <TriplesSectionHeader
                  label={t(
                    i18nLocale,
                    'viewpointBuilder:editTraversal.incoming',
                    'Incoming'
                  )}
                  count={tripleOptions.incoming.length}
                />
                <TripleOptionsList
                  options={tripleOptions.incoming}
                  hasTripleOptions={hasTripleOptions.incomingReferences}
                  direction={ReferenceDirection.INCOMING}
                  toggleOption={commands.toggleTripleOption}
                  selectedComponent={tripleOptions.selectedNode}
                  isDisabledWholeRow={isDisabledWholeRow}
                  i18nLocale={i18nLocale}
                />
                <HiddenTriplesHint
                  hiddenTriplesCount={
                    triplesSortAndFiltersState.incomingTriplesHiddenCount
                  }
                  onShowAllClicked={
                    commands.toggleShowOnlyOptionsWithInstanceCounts
                  }
                  i18nLocale={i18nLocale}
                />
              </>
            )}
          </Stack>
        </Box>
      </EnforceContentWidth>
      {isLoadingOptionCounts && <Scrim />}
    </OverlayContainer>
  );
};
